import React from "react";
import { useTranslation } from "react-i18next";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";
import { Action } from "../types";

const CODE_LENGTH = 4;

type Props = {
    code: string;
    loading: boolean;
    error: string | null;
    dispatch: React.Dispatch<Action>;
    codeInputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
    onSubmit: (event: React.FormEvent) => void;
    theme: "light" | "dark";
};

const STYLES = {
    light: {
        digit: "w-10 h-12 bg-white/80 border border-current/20 rounded-xl text-center text-xl font-changa-one outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-colors",
        submit: "w-full h-10 rounded-xl bg-blue-900 text-white font-changa-one text-xs tracking-[0.12em] uppercase cursor-pointer hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
        secondary: "flex-1 h-10 rounded-xl bg-white/20 font-changa-one text-xs tracking-[0.12em] uppercase cursor-pointer hover:bg-white/30 transition-colors",
    },
    dark: {
        digit: "w-10 h-12 bg-white/5 border border-white/10 rounded-xl text-white text-center text-xl font-changa-one outline-none focus:border-white/25 transition-colors",
        submit: "w-full h-10 rounded-xl bg-white text-gray-900 font-changa-one text-xs tracking-[0.12em] uppercase cursor-pointer hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
        secondary: "flex-1 h-10 rounded-xl bg-white/5 text-white/50 font-changa-one text-xs tracking-[0.12em] uppercase cursor-pointer hover:bg-white/10 transition-colors",
    },
};

const CodeStep = ({ code, loading, error, dispatch, codeInputRefs, onSubmit, theme }: Props) => {
    const { t } = useTranslation();
    const styles = STYLES[theme];

    return (
        <>
            <div className="flex flex-col items-center gap-0.5">
                <p className="font-changa-one text-base text-center">
                    {t("signup.code_title")}
                </p>
                <p className="text-sm leading-snug text-center italic opacity-60">
                    {t("signup.code_spam_hint")}
                </p>
            </div>
            <p className="text-sm leading-snug text-center opacity-60">
                {t("signup.code_description")}
            </p>

            <form onSubmit={onSubmit} className="w-full flex flex-col gap-3">
                <div className="flex items-center justify-center gap-2">
                    {Array.from({ length: CODE_LENGTH }).map((_, index) => (
                        <input
                            key={index}
                            ref={(element) => { codeInputRefs.current[index] = element; }}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete={index === 0 ? "one-time-code" : "off"}
                            maxLength={1}
                            value={code[index] || ""}
                            onChange={(event) => {
                                const digit = event.target.value.replace(/\D/g, "").slice(-1);
                                const updated = code.split("");

                                updated[index] = digit;

                                const joined = updated.join("").slice(0, CODE_LENGTH);
                                dispatch({ type: "SET_CODE", code: joined });

                                if(digit && index < CODE_LENGTH - 1) {
                                    codeInputRefs.current[index + 1]?.focus();
                                }
                            }}
                            onKeyDown={(event) => {
                                if(event.key === "Backspace" && !code[index] && index > 0) {
                                    codeInputRefs.current[index - 1]?.focus();
                                }
                            }}
                            onPaste={(event) => {
                                event.preventDefault();
                                const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);

                                if(pasted) {
                                    dispatch({ type: "SET_CODE", code: pasted });
                                    codeInputRefs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
                                }
                            }}
                            className={styles.digit}
                        />
                    ))}
                </div>
                {error && (
                    <p className="text-rose-400 text-[10px] text-center">{error}</p>
                )}
                <ButtonHapticWrapper>
                    <button
                        type="submit"
                        disabled={loading || code.length !== CODE_LENGTH}
                        className={styles.submit}
                    >
                        {loading ? "..." : t("signup.code_submit")}
                    </button>
                </ButtonHapticWrapper>
            </form>
        </>
    );
};

export default CodeStep;
